import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Rocket, Plus, Trash2, ClipboardList, ExternalLink, BookOpen, BellRing, Loader2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import OnboardingTemplateEditor from "@/components/OnboardingTemplateEditor";
import WorkLocationPanel from "@/components/WorkLocationPanel";

const STATUS = { active: "bg-[#DBEAFE] text-[#0A2A5E]", overdue: "bg-rose-100 text-rose-800", complete: "bg-emerald-100 text-emerald-800" };

function NewPlanDialog({ open, onOpenChange, onCreated }) {
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState({ employee_id: "", start_date: new Date().toISOString().slice(0, 10), role_title: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) api.get("/onboarding/candidates").then((r) => setPeople(r.data.filter((p) => !p.has_plan))); }, [open]);
  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/onboarding/plans", form); toast.success(`Plan created for ${data.employee_name}`); onCreated(); onOpenChange(false); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not create plan"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">Start a 30/60/90-day onboarding plan</DialogTitle><DialogDescription>Generates the standard checklist (I-9/W-4, benefits, policy acknowledgements, training record, check-ins) with due dates from the start date. You can add or remove items afterwards.</DialogDescription></DialogHeader>
        <form onSubmit={create} className="space-y-3">
          <div>
            <Label>New hire</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="onboarding-employee-select" className="mt-1"><SelectValue placeholder="Select a person…" /></SelectTrigger>
              <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} · {p.email} ({p.role})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start date</Label><Input data-testid="onboarding-start-date" type="date" required value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1" /></div>
            <div><Label>Role title (optional)</Label><Input data-testid="onboarding-role-title" value={form.role_title} onChange={(e) => setForm({ ...form, role_title: e.target.value })} className="mt-1" placeholder="HR Analyst" /></div>
          </div>
          <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="onboarding-create-submit" type="submit" disabled={busy || !form.employee_id} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{busy ? "Creating…" : "Create plan"}</Button></div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AddItem({ plan, phase, onChanged }) {
  const [title, setTitle] = useState("");
  const add = async () => {
    if (!title.trim()) return;
    try { await api.post(`/onboarding/plans/${plan.id}/items`, { phase, title: title.trim(), owner: "employee" }); setTitle(""); onChanged(); }
    catch { toast.error("Could not add item"); }
  };
  return (
    <div className="flex gap-2 mt-2">
      <Input data-testid={`add-item-${plan.id}-${phase}`} value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a custom item…" className="h-8 text-xs" />
      <Button size="sm" variant="outline" data-testid={`add-item-btn-${plan.id}-${phase}`} onClick={add} className="h-8"><Plus className="w-3.5 h-3.5" /></Button>
    </div>
  );
}

function PlanCard({ plan, isManager, onChanged, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const toggle = async (item, done) => {
    try { await api.patch(`/onboarding/plans/${plan.id}/items/${item.id}`, { done }); onChanged(); }
    catch { toast.error("Could not update item"); }
  };
  const removeItem = async (item) => {
    try { await api.delete(`/onboarding/plans/${plan.id}/items/${item.id}`); onChanged(); } catch { toast.error("Could not remove"); }
  };
  const removePlan = async () => {
    if (!window.confirm(`Delete ${plan.employee_name}'s onboarding plan?`)) return;
    try { await api.delete(`/onboarding/plans/${plan.id}`); toast.success("Plan deleted"); onChanged(); } catch { toast.error("Could not delete"); }
  };
  return (
    <Card data-testid={`plan-${plan.id}`} className="bg-white border-slate-200 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full text-left p-5 flex flex-wrap items-center gap-4 hover:bg-slate-50">
        <div className="w-10 h-10 rounded-lg bg-[#FDECD0] flex items-center justify-center"><Rocket className="w-5 h-5 text-[#7C3F00]" /></div>
        <div className="flex-1 min-w-[12rem]">
          <div className="font-display font-bold text-[#0A2A5E]">{plan.employee_name}{plan.role_title && <span className="text-slate-500 font-normal"> · {plan.role_title}</span>}</div>
          <div className="text-xs text-slate-500">Started {plan.start_date} · {plan.done_count}/{plan.total} items · created by {plan.created_by_name}</div>
        </div>
        <span data-testid={`plan-status-${plan.id}`} className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS[plan.status]}`}>{plan.status}</span>
        <div className="w-full sm:w-48"><Progress data-testid={`plan-progress-${plan.id}`} value={plan.percent} className="h-2" /><div className="text-[11px] text-slate-500 mt-1 text-right">{plan.percent}% complete</div></div>
      </button>
      {open && (
        <div className="px-5 pb-5 grid md:grid-cols-3 gap-4">
          {plan.phases.map((ph) => (
            <div key={ph.key} data-testid={`phase-${plan.id}-${ph.key}`} className={`rounded-xl border p-4 ${ph.complete ? "border-emerald-200 bg-emerald-50/40" : ph.overdue ? "border-rose-200 bg-rose-50/40" : "border-slate-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <div><div className="font-display font-bold text-[#0A2A5E] text-sm">{ph.title}</div><div className="text-[11px] text-slate-500">Due {ph.due_date} · {ph.done}/{ph.total}</div></div>
                {ph.overdue && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">Overdue</span>}
                {ph.complete && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Done</span>}
              </div>
              <ul className="mt-3 space-y-2">
                {plan.items.filter((i) => i.phase === ph.key).map((it) => (
                  <li key={it.id} data-testid={`item-${it.id}`} className="flex items-start gap-2 text-sm">
                    <Checkbox data-testid={`item-check-${it.id}`} checked={it.done} onCheckedChange={(v) => toggle(it, !!v)} className="mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className={`leading-snug ${it.done ? "line-through text-slate-400" : "text-slate-800"}`}>{it.title}</div>
                      {it.description && !it.done && <div className="text-[11px] text-slate-500 mt-0.5">{it.description}</div>}
                      <div className="flex flex-wrap gap-2 mt-1 text-[11px]">
                        <span className={`px-1.5 py-0.5 rounded ${it.owner === "manager" ? "bg-[#0A2A5E] text-white" : "bg-slate-100 text-slate-600"}`}>{it.owner === "manager" ? "Manager" : "New hire"}</span>
                        {it.form_id && !it.done && <Link to={`/app/forms/fill/${it.form_id}`} data-testid={`item-form-${it.id}`} className="text-[#0A2A5E] font-semibold hover:underline inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Open form (auto-completes)</Link>}
                        {it.form_id && it.done && it.submission_id && <Link to={`/app/forms/submissions/${it.submission_id}`} className="text-emerald-700 font-semibold hover:underline">View submission</Link>}
                        {it.link && <Link to={it.link} className="text-[#0A2A5E] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Open</Link>}
                        {it.source_doc && <Link to="/app/documents" className="text-slate-500 hover:underline inline-flex items-center gap-1"><BookOpen className="w-3 h-3" /> Source</Link>}
                      </div>
                    </div>
                    {isManager && <button data-testid={`item-delete-${it.id}`} onClick={() => removeItem(it)} className="text-slate-300 hover:text-rose-600" aria-label="Remove item"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </li>
                ))}
              </ul>
              {isManager && <AddItem plan={plan} phase={ph.key} onChanged={onChanged} />}
            </div>
          ))}
          {isManager && <div className="md:col-span-3 flex justify-end"><Button size="sm" variant="ghost" data-testid={`plan-delete-${plan.id}`} onClick={removePlan} className="text-rose-600 text-xs"><Trash2 className="w-3.5 h-3.5 mr-1" /> Delete plan</Button></div>}
        </div>
      )}
    </Card>
  );
}

export default function Onboarding() {
  const { user } = useAuth();
  const isManager = user?.role === "manager";
  const [plans, setPlans] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/onboarding/plans").then((r) => setPlans(r.data.plans)).catch(() => toast.error("Could not load onboarding")), []);
  useEffect(() => { load(); }, [load]);

  const runReminders = async () => {
    setRunning(true);
    try { const { data } = await api.post("/onboarding/reminders/run"); toast.success(`${data.reminders} reminder(s) sent`); }
    catch { toast.error("Could not run reminders"); } finally { setRunning(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#C7761A] font-bold">Onboarding</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0A2A5E]">{isManager ? "New hire onboarding plans" : "My Onboarding"}</h1>
          <p className="text-sm text-slate-600 mt-1">Guided 30/60/90-day checklist. Form-linked items complete automatically when the form is submitted; reminders go out 3 days before each phase deadline.
            {" "}<Link to="/app/practices?g=bp-onboarding" data-testid="onboarding-sop-link" className="text-[#0A2A5E] font-semibold hover:underline">Read the onboarding SOP →</Link></p>
        </div>
        {isManager && (
          <div className="flex flex-wrap gap-2">
            <OnboardingTemplateEditor />
            <Button data-testid="onboarding-run-reminders" variant="outline" onClick={runReminders} disabled={running} className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0]">{running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />} Send due reminders</Button>
            <Button data-testid="onboarding-new-btn" onClick={() => setNewOpen(true)} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white"><UserPlus className="w-4 h-4 mr-2" /> New onboarding plan</Button>
          </div>
        )}
      </div>

      {isManager && <WorkLocationPanel />}
      {isManager && plans.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[["active", "Active"], ["overdue", "Overdue"], ["complete", "Complete"]].map(([k, l]) => (
            <Card key={k} data-testid={`onboarding-stat-${k}`} className={`p-4 border-0 ${STATUS[k]}`}><div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{l}</div><div className="font-display text-3xl font-extrabold mt-1">{plans.filter((p) => p.status === k).length}</div></Card>
          ))}
        </div>
      )}

      <div className="space-y-4">
        {plans.map((p, i) => <PlanCard key={p.id} plan={p} isManager={isManager} onChanged={load} defaultOpen={!isManager || i === 0} />)}
        {plans.length === 0 && (
          <Card data-testid="onboarding-empty" className="p-10 text-center text-slate-500 bg-white border-slate-200">
            <Rocket className="w-8 h-8 mx-auto mb-2 opacity-50" />
            {isManager ? "No onboarding plans yet. Create one for a new hire to generate the 30/60/90-day checklist." : "You don't have an onboarding plan yet — your HR manager will create one when you join."}
          </Card>
        )}
      </div>
      <NewPlanDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
    </div>
  );
}
